import { camelCase } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import AnswerPerformanceView from 'models/location/answer_performance_view';
import connect from 'components/lib/connect';
import GuidePerformanceTable from 'components/guide_performance/guide_performance_table';
import GuidePerformanceView from 'models/location/guide_performance_view';
import { H1 } from 'components/common/headers';
import LoadingDots from 'components/lib/loading_dots';
import NavigateTo from 'actions/current_location/navigate_to';
import PageLayout from 'components/page_layout';
import PerformanceDateRange from 'models/performance_date_range';
import AnswersStrokeIcon from '../common/icons/stroke/answers-stroke';

export function GuidePerformance({
  handoffPercent,
  isLoading,
  sessionCount,
  currentLocation,
  navigateToAnswerPerformance,
}) {
  const statBox = ({ text, description, onClick, icon }) => {
    const dataAid = camelCase(description);
    return (
      <StatBox clickable={!!onClick} data-aid={dataAid} onClick={onClick}>
        <StatText>
          {isLoading ? (
            <LoadingDotsWrapper data-aid={`loadingDots-${dataAid}`}>
              <LoadingDots />
            </LoadingDotsWrapper>
          ) : (
            <span data-aid={`statText-${dataAid}`}>{icon || text}</span>
          )}
        </StatText>
        <span data-aid={`statDescription-${dataAid}`}>{description}</span>
      </StatBox>
    );
  };

  const handleAnswerPerformanceClick = () => {
    const dateRange = currentLocation.dateRange || PerformanceDateRange.LAST_1_DAY;
    navigateToAnswerPerformance && navigateToAnswerPerformance(dateRange);
  };

  return (
    <PageLayout className="guide-performance-pageWrapper" id="guide-performance">
      <GuidePerformancePage>
        <H1>Journeys</H1>
        <p data-aid="guidePerformance-description">
          See how well your Guides are working with clear insights on engagement and effectiveness. Find out what’s
          working, what needs improvement, and where to focus your updates to create a better experience for your
          customers.{' '}
          <a
            data-aid="guidePerformance-helpLink"
            href="https://help.gladly.com/docs/journeys-for-guides"
            rel="noopener noreferrer"
            target="_blank"
          >
            Learn more.
          </a>
        </p>

        <StatsContainer>
          {statBox({
            text: sessionCount ? Number(sessionCount).toLocaleString() : 0,
            description: 'Customer inquiries',
          })}
          {statBox({ text: `${sessionCount ? 100 - handoffPercent : 0}%`, description: 'Resolutions' })}
          {statBox({ text: `${handoffPercent}%`, description: 'Handoffs' })}

          {statBox({
            icon: (
              <IconWrapper>
                <AnswersStrokeIcon />
              </IconWrapper>
            ),
            description: 'Answers performance',
            onClick: handleAnswerPerformanceClick,
          })}
        </StatsContainer>
        <GuidePerformanceTable currentLocation={currentLocation} />
      </GuidePerformancePage>
    </PageLayout>
  );
}

GuidePerformance.propTypes = {
  handoffPercent: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
  sessionCount: PropTypes.number.isRequired,
  currentLocation: PropTypes.instanceOf(GuidePerformanceView).isRequired,
  navigateToAnswerPerformance: PropTypes.func,
};

function mapStateToProps({ getProvider }) {
  let handoffsCount = 0,
    sessionCount = 0;

  const guidePerformancesProvider = getProvider('guidePerformances');
  const currentLocation = getProvider('currentLocation').get();
  const isLoading = guidePerformancesProvider.isLoading();
  if (!isLoading) {
    guidePerformancesProvider.findAll().forEach(item => {
      handoffsCount += item.handoffs;
      sessionCount += item.references;
    });
  }

  const handoffPercent = sessionCount ? Math.floor((handoffsCount / sessionCount) * 100) : 0;

  return {
    handoffPercent,
    isLoading,
    sessionCount,
    currentLocation,
  };
}

function mapExecuteToProps(executeAction) {
  return {
    navigateToAnswerPerformance: dateRange => {
      executeAction(NavigateTo, AnswerPerformanceView.create({ dateRange }));
    },
  };
}

const GuidePerformanceContainer = connect(mapStateToProps, mapExecuteToProps)(GuidePerformance);

export default GuidePerformanceContainer;

const GuidePerformancePage = styled.div`
  margin: 40px 64px;
`;

const LoadingDotsWrapper = styled.div`
  padding: 12px 0 ${p => p.theme.spacing.small} 0;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${p => p.theme.spacing.insetMedium};
  margin: ${p => p.theme.spacing.xlarge} 0;
  width: 100%;
  max-width: 900px; /* Accommodate 4 tiles comfortably */
`;

const StatBox = styled.div`
  border: 1px solid ${p => p.theme.colors.gray300};
  padding: ${p => p.theme.spacing.insetMedium};
  text-align: center;
  border-radius: ${p => p.theme.borderRadius.large};
  cursor: ${p => (p.clickable ? 'pointer' : 'default')};
  min-height: 80px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    ${p =>
      p.clickable &&
      `
      border-color: ${p.theme.colors.green400};
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    `}
  }

  &:focus {
    ${p =>
      p.clickable &&
      `
      outline: none;
      border-color: ${p.theme.colors.green400};
      box-shadow: 0 0 0 2px ${p.theme.colors.green400}40;
    `}
  }
`;

const StatText = styled.div`
  font-weight: bold;
`;

const IconWrapper = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${p => p.theme.spacing.xsmall} auto;

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }
`;
